import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaStar, FaShoppingCart, FaClock, FaSearch } from 'react-icons/fa';
import API from '../api/axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Services = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, categoriesRes] = await Promise.allSettled([
          API.get('/services'), API.get('/categories')
        ]);
        if (servicesRes.status === 'fulfilled') setServices(servicesRes.value.data);
        if (categoriesRes.status === 'fulfilled') setCategories(categoriesRes.value.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, []);

  const filteredServices = services.filter(s => {
    const matchCategory = activeCategory === 'all' || s.category?._id === activeCategory;
    const matchSearch = s.title.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  // Default services for display when backend is empty
  const defaultServices = [
    { _id: '1', title: 'Custom Website Development', shortDescription: 'Professional website built with modern technologies', price: 500, deliveryTime: 7, rating: 4.9, totalReviews: 24, totalOrders: 45, features: ['Responsive Design', 'SEO Optimized', 'Fast Loading'] },
    { _id: '2', title: 'E-Commerce Store Setup', shortDescription: 'Complete online store with payment integration', price: 800, deliveryTime: 14, rating: 4.8, totalReviews: 18, totalOrders: 32, features: ['Product Management', 'Payment Gateway', 'Order Tracking'] },
    { _id: '3', title: 'API Development', shortDescription: 'RESTful API design and development', price: 300, deliveryTime: 5, rating: 5.0, totalReviews: 12, totalOrders: 28, features: ['Authentication', 'Documentation', 'Testing'] },
    { _id: '4', title: 'Bug Fixing & Optimization', shortDescription: 'Fix bugs and improve performance', price: 100, deliveryTime: 2, rating: 4.7, totalReviews: 30, totalOrders: 60, features: ['Code Review', 'Performance Boost', 'Security Fixes'] },
    { _id: '5', title: 'Full Stack Application', shortDescription: 'Complete MERN stack application from scratch', price: 1200, deliveryTime: 21, rating: 4.9, totalReviews: 10, totalOrders: 15, features: ['Frontend + Backend', 'Database Design', 'Deployment'] },
    { _id: '6', title: 'UI/UX Design to Code', shortDescription: 'Convert your design to responsive code', price: 250, deliveryTime: 4, rating: 4.6, totalReviews: 20, totalOrders: 38, features: ['Pixel Perfect', 'Animations', 'Cross Browser'] }
  ];

  const displayServices = filteredServices.length > 0 ? filteredServices : defaultServices;

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: '100px', minHeight: '100vh' }}>
        <div className="container">
          <div className="section-title">
            <h2>Our Services</h2>
            <p>Professional solutions for your business needs</p>
          </div>

          {/* Search & Filter */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '40px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <div style={{ position: 'relative', flex: '1', maxWidth: '400px' }}>
              <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="text" placeholder="Search services..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', padding: '12px 18px 12px 40px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => setActiveCategory('all')} className={`btn btn-small ${activeCategory === 'all' ? 'btn-primary' : 'btn-outline'}`}>All</button>
              {categories.map(cat => (
                <button key={cat._id} onClick={() => setActiveCategory(cat._id)} className={`btn btn-small ${activeCategory === cat._id ? 'btn-primary' : 'btn-outline'}`}>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Services Grid */}
          <div className="projects-grid">
            {displayServices.map((service, idx) => (
              <motion.div key={service._id} className="project-card"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}>
                <div className="project-image" style={{ height: '150px', background: `linear-gradient(135deg, hsl(${(idx * 60) % 360}, 70%, 50%), hsl(${(idx * 60 + 60) % 360}, 70%, 40%))` }}>
                  <span style={{ fontSize: '2.5rem', position: 'relative', zIndex: 1 }}>
                    {['💻', '🛒', '⚡', '🔧', '🚀', '🎨'][idx % 6]}
                  </span>
                </div>
                <div className="project-info">
                  <h3>{service.title}</h3>
                  <p>{service.shortDescription || service.description?.substring(0, 100)}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '12px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FaStar style={{ color: '#ffc107' }} /> {service.rating || 5.0} ({service.totalReviews || 0})
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FaClock /> {service.deliveryTime} days
                    </span>
                    <span><FaShoppingCart /> {service.totalOrders || 0} orders</span>
                  </div>
                  {service.features && (
                    <div className="project-tech">
                      {service.features.slice(0, 3).map((f, i) => <span key={i}>{f}</span>)}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                    <div>
                      {service.discountPrice > 0 && <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', marginRight: '8px', fontSize: '0.9rem' }}>${service.price}</span>}
                      <span style={{ fontSize: '1.4rem', fontWeight: '700', background: 'var(--gradient-1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        ${service.discountPrice || service.price}
                      </span>
                    </div>
                    <Link to={`/services/${service._id}`} className="btn btn-primary btn-small">View Details</Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {loading && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>Loading services...</p>}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Services;
